import {ChangeDetectorRef, Component, Input, OnInit} from "@angular/core";
import {MatCardModule} from "@angular/material/card";
import {MatFormFieldModule} from "@angular/material/form-field";
import {TablerIconsModule} from "angular-tabler-icons";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatInputModule} from "@angular/material/input";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {CommonModule} from "@angular/common";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {KeycloakService} from "./keycloak.service";
import {KeycloakMatchingDTO} from "./models/keycloak-matching.dto";
import {Router} from '@angular/router';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {HttpClient} from '@angular/common/http';
import {LocalizationPipe} from "../../../pipe/localization-pipe";
import {retry, delay} from 'rxjs/operators';
import {LocalizationService} from "../../../services/localizationService";
import {OrganisationDTO} from "../../../DTO/Organisation/OrganisationDTO";

@Component({
  selector: 'app-keycloak-matching',
  templateUrl: 'keycloak-matching.html',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    TablerIconsModule,
    MatSlideToggleModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    LocalizationPipe
  ],
  styleUrls: ['./keycloak-matching.scss']
})
export class KeycloakMatching implements OnInit {
  @Input() selectedOrganisationId: number;
  @Input() loadOnInit: boolean = true;
  settingsForm: FormGroup;
  initialFormValue: any;
  displayName: string
  selectedOrganisation: OrganisationDTO;
  hasLoadedData: boolean = false;
  loading: boolean = false;

  constructor(
      private fb: FormBuilder,
      private keycloakService: KeycloakService,
      private router: Router,
      private snackBar: MatSnackBar,
      private localizationService: LocalizationService,
      private cdr: ChangeDetectorRef,
      private http: HttpClient
  ) {

    this.settingsForm = this.fb.group({
      subDomain: [''],
      authorizationUrl: [''],
      tokenUrl: [''],
      logoutUrl: [''],
      userInfoUrl: [''],
      issuer: [''],
      JwksUrl: [''],
      ClientId: [''],
      ClientSecret: [''],
      displayName: [this.displayName + ' SSO'],
      domain: [''],
      organisationDTO: [new OrganisationDTO()],
    });
    this.settingsForm.get('organisationDTO').setValue(this.selectedOrganisation);
    this.initialFormValue = this.settingsForm.value;
  }

  onSave() {
    if (this.settingsForm.valid) {
      this.loading = true;
      const formData = new KeycloakMatchingDTO({
        ...this.settingsForm.value,
        jwksUrl: this.settingsForm.value.JwksUrl,
        clientId: this.settingsForm.value.ClientId,
        clientSecret: this.settingsForm.value.ClientSecret,
        organisationDTO: this.selectedOrganisation
      });
      this.keycloakService.save(formData).subscribe({
        next: (response) => {
          if (response && response['authorizationUrl']) {
            const subdomain = this.settingsForm.value.subDomain;

            if (subdomain) {
              // Subdomain'e göre yeni URL oluştur
              const newUrl = `http://${subdomain}.bilgebot.ai/apps/keycloak/role-mapping?subdomain=${subdomain}`;
              
              // URL'nin erişilebilir olup olmadığını kontrol et
              this.checkUrlAvailability(newUrl);
            } else {
              // Subdomain yoksa normal navigate
              this.loading = false;
              this.router.navigate(['apps/keycloak/role-mapping']);
            }
          } else {
            this.snackBar.open(
                this.localizationService.getLocalizationValue('KeyCloak.Messages.SaveError') +
                (response.message || this.localizationService.getLocalizationValue('KeyCloak.Messages.UnknownError')),
                this.localizationService.getLocalizationValue('KeyCloak.Messages.Close'),
                {
                  duration: 5000,
                  horizontalPosition: 'end',
                  verticalPosition: 'top',
                  panelClass: ['error-snackbar']
                }
            );
          }
        },
        error: (error) => {
          this.loading = false;
        }
      });
    }
  }

  onRevert() {
    this.settingsForm.reset(this.initialFormValue);
  }

  checkUrlAvailability(url: string, attemptCount: number = 0) {
    const maxAttempts = 10; // Maksimum 10 deneme (50 saniye)
    
    if (attemptCount >= maxAttempts) {
      // Maksimum deneme sayısına ulaşıldı, yönlendir
      this.loading = false;
      window.location.href = url;
      return;
    }

    // Image element ile ping yap (CORS sorununu önler)
    const img = new Image();
    
    img.onload = () => {
      // Sayfa yüklenebiliyor, yönlendir
      this.loading = false;
      window.location.href = url;
    };
    
    img.onerror = () => {
      // 5 saniye bekle ve tekrar dene
      setTimeout(() => {
        this.checkUrlAvailability(url, attemptCount + 1);
      }, 5000);
    };
    
    // Favicon'u kontrol et (her sitede vardır)
    img.src = `http://${url.split('/')[2]}/favicon.ico?t=${Date.now()}`;
  }

  ngOnInit(): void {
    if (this.loadOnInit || this.loadOnInit == undefined) {
      this.loadKeycloakSettings();
    }
  }

  loadKeycloakSettings(): void {
    if (this.selectedOrganisationId) {
      this.selectedOrganisation = new OrganisationDTO();
      this.selectedOrganisation.id = this.selectedOrganisationId;
    } else if (window.globalActiveUser && window.globalActiveUser.organisationAccount && window.globalActiveUser.organisationAccount.id) {
      this.selectedOrganisation = new OrganisationDTO();
      this.selectedOrganisation.id = window.globalActiveUser.organisationAccount.id;
    }
    this.keycloakService.getKeycloakMatchingByOrganizationId(this.selectedOrganisation.id).subscribe({
      next: (data) => {
        if (data) {
          this.hasLoadedData = true;
          this.settingsForm.patchValue({
            authorizationUrl: data.authorizationUrl,
            tokenUrl: data.tokenUrl,
            logoutUrl: data.logoutUrl,
            userInfoUrl: data.userInfoUrl,
            issuer: data.issuer,
            JwksUrl: data.jwksUrl,
            ClientId: data.clientId,
            ClientSecret: data.clientSecret,
            displayName: data.displayName || (this.displayName + ' SSO'),
            domain: data.domain,
            subDomain:data.subDomain
          });
          this.initialFormValue = this.settingsForm.value;
        }
      },
      error: (error) => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }
}
